/**
 * Ausblenden (reversible) or Entfernen (hard delete) an account
 * (ACCOUNT-REMOVAL-SPEC §9).
 *
 * The caller states the intent up front via `mode` — the two actions have their
 * own entry points in the Konten card menu, so this dialog no longer opens on a
 * mode radio that made „Ausblenden" reachable only by first clicking „Entfernen".
 * Delete mode still offers a one-click de-escalation to hide, which is the only
 * remaining way the mode changes while open.
 *
 * Preview-before-write gating is copied from `MergeAccountsDialog`: the
 * destructive confirm button does not exist at all until a dry-run preview for
 * the CURRENT mode has loaded. Delete mode additionally requires a confirmation
 * checkbox before its (danger-styled) confirm button enables.
 *
 * The stateful body is mounted only while open, so state initialises fresh from
 * the current props each time (no reset effect) — same pattern as
 * `MergeAccountsDialog`.
 */
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { InlineError } from '../../components/ui/InlineError.tsx';
import { WarningList } from '../../components/ui/WarningList.tsx';
import { ApiRequestError } from '../../lib/api.ts';
import { accountChannelLabel } from '../../lib/labels.ts';
import { useRemoveAccount } from '../../lib/hooks/useAccountMutations.ts';
import type { AccountOut, AccountRemoveMode, RemovalReport } from '../../lib/types.ts';

export interface RemoveAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: AccountOut | null;
  /** Which action the user picked in the card menu — the dialog opens on it. */
  mode: AccountRemoveMode;
}

export function RemoveAccountDialog({
  open,
  onOpenChange,
  account,
  mode,
}: RemoveAccountDialogProps) {
  if (!open || !account) return null;
  return <RemoveForm account={account} initialMode={mode} onClose={() => onOpenChange(false)} />;
}

function RemoveForm({
  account,
  initialMode,
  onClose,
}: {
  account: AccountOut;
  initialMode: AccountRemoveMode;
  onClose: () => void;
}) {
  const remove = useRemoveAccount();
  const [mode, setMode] = useState<AccountRemoveMode>(initialMode);
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState<RemovalReport | null>(null);
  const [result, setResult] = useState<RemovalReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A preview is only trustworthy for the exact mode it was requested for.
  const previewCurrent = preview !== null && preview.mode === mode;
  const canConfirm = mode === 'hide' || confirmed;

  function describeError(err: unknown): string {
    return err instanceof ApiRequestError
      ? err.message
      : mode === 'hide'
        ? 'Ausblenden fehlgeschlagen. Bitte erneut versuchen.'
        : 'Entfernen fehlgeschlagen. Bitte erneut versuchen.';
  }

  /** De-escalate delete → hide; the loaded preview no longer applies. */
  function softenToHide() {
    setMode('hide');
    setPreview(null);
    setConfirmed(false);
  }

  function loadPreview() {
    setError(null);
    remove.mutate(
      { id: account.id, body: { mode, dry_run: true } },
      {
        onSuccess: setPreview,
        onError: (err) => setError(describeError(err)),
      },
    );
  }

  function confirmRemove() {
    setError(null);
    remove.mutate(
      { id: account.id, body: { mode } },
      {
        onSuccess: setResult,
        onError: (err) => setError(describeError(err)),
      },
    );
  }

  if (result) {
    return (
      <Modal
        open
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        title={result.mode === 'hide' ? 'Konto ausgeblendet' : 'Konto entfernt'}
        description={account.name}
        size="md"
        footer={<Button onClick={onClose}>Schließen</Button>}
      >
        <div className="space-y-4">
          <ReportSummary report={result} />
          <WarningList warnings={result.warnings} />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={mode === 'hide' ? 'Konto ausblenden' : 'Konto entfernen'}
      description={`${account.name} (${accountChannelLabel(account)}, ${account.transaction_count} Buchungen)`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Abbrechen
          </Button>
          {previewCurrent ? (
            <Button
              variant={mode === 'delete' ? 'danger' : 'primary'}
              onClick={confirmRemove}
              disabled={remove.isPending || !canConfirm}
            >
              {mode === 'hide' ? 'Ausblenden' : 'Endgültig löschen'}
            </Button>
          ) : (
            <Button onClick={loadPreview} disabled={remove.isPending}>
              Vorschau laden
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <InlineError message={error} />

        {mode === 'hide' ? (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Konto und alle Buchungen verschwinden aus Listen und Auswertungen; der Abgleich mit der
            Bank pausiert. Die Daten bleiben gespeichert und lassen sich jederzeit über „Wieder
            einblenden" zurückholen.
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Konto, alle Buchungen und Verknüpfungen werden unwiderruflich gelöscht. Belege bleiben
              erhalten und wandern zurück in den Beleg-Eingang. Es gibt keine Wiederherstellung —
              nur das nächtliche Backup.
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Du willst die Daten behalten und das Konto nur aus den Auswertungen nehmen?{' '}
              <button
                type="button"
                onClick={softenToHide}
                className="font-semibold text-sky-700 underline underline-offset-2 transition-colors duration-150 hover:text-sky-900 dark:text-sky-400 dark:hover:text-sky-300"
              >
                Stattdessen ausblenden
              </button>
            </p>
          </>
        )}

        {previewCurrent ? (
          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Vorschau
            </p>
            <ReportSummary report={preview} />
            <WarningList warnings={preview.warnings} />
          </div>
        ) : null}

        {mode === 'delete' ? (
          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600"
            />
            <span>Ich verstehe, dass dies nicht rückgängig gemacht werden kann.</span>
          </label>
        ) : null}
      </div>
    </Modal>
  );
}

function ReportSummary({ report }: { report: RemovalReport }) {
  if (report.mode === 'hide') {
    return (
      <p className="text-sm text-slate-700 dark:text-slate-300">
        Das Konto und seine Buchungen werden ausgeblendet — jederzeit über „Wieder einblenden"
        umkehrbar.
      </p>
    );
  }

  const parts: string[] = [`${report.transactions_deleted} Buchungen werden gelöscht`];
  if (report.transfer_groups_unlinked > 0) {
    parts.push(
      `${report.transfer_groups_unlinked} Überweisungs-Verknüpfungen werden gelöst — die Gegenseite wandert zurück in den Eingang`,
    );
  }
  if (report.contracts_deleted > 0) {
    parts.push(`${report.contracts_deleted} Verträge werden gelöscht`);
  }
  if (report.receipts_detached > 0) {
    parts.push(`${report.receipts_detached} Belege wandern zurück in den Beleg-Eingang`);
  }
  if (report.import_batches_deleted > 0) {
    parts.push(`${report.import_batches_deleted} Import-Stapel werden gelöscht`);
  }
  if (report.email_messages_reset > 0) {
    parts.push(`${report.email_messages_reset} E-Mail-Nachrichten werden zurückgesetzt`);
  }
  if (report.email_configs_deactivated > 0) {
    parts.push('Die E-Mail-Benachrichtigung (Kontowecker) wird deaktiviert');
  }
  parts.push(
    report.row_deleted
      ? 'Das Konto wird vollständig gelöscht'
      : 'Das Konto bleibt als Platzhalter bestehen, damit es bei einer erneuten Verbindung nicht wieder auftaucht',
  );

  return (
    <ul className="list-inside list-disc space-y-0.5 text-sm text-slate-700 dark:text-slate-300">
      {parts.map((part) => (
        <li key={part}>{part}</li>
      ))}
    </ul>
  );
}
